import { type JSX, useEffect, useState } from 'react';

import { ExportsApi } from 'Api/Exports/ExportsApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { DataGrid, GridColumn, dateColumn } from 'Components/DataGrid/DataGrid';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { EXPORTS } from 'Config/Paths';
import { getUserNameFromSubject } from 'Helpers/UserUtils';
import { Action, AssociatedRisk } from 'Models/Actions';
import { UploadedFile } from 'Models/Files';

export interface ActionsExportProps {
    exportsApi: ExportsApi;
    disableVirtualization?: boolean;
}

export const ActionsExport = (props: ActionsExportProps): JSX.Element => {
    const [actions, setActions] = useState<Action[]>();
    const [error, setError] = useState<string>();
    const cachedData = useCachedData();

    // Save the Tags map locally so it can be repeatedly accessed rather than recreating it as the rows and Tags are looped through.
    const tagsMap = cachedData.getTagsMap();

    useEffect(() => {
        const getActions = async (): Promise<void> => {
            try {
                const thirdPartyResponse = await props.exportsApi.getActionsExport();
                setActions(thirdPartyResponse.data);
            } catch (error) {
                setError(error.message);
            }
        };

        getActions();
    }, [props.exportsApi]);

    const columns: GridColumn<Action>[] = [
        { field: 'title', headerName: 'Action Title', width: 300 },
        { field: 'description', headerName: 'Description', width: 300 },
        { field: 'status', headerName: 'Status', width: 300 },
        { field: 'owner', headerName: 'Owner', width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        {
            field: 'delegates',
            headerName: 'Delegates',
            width: 300,
            valueGetter: (value) =>
                value
                    .map((userId: string) => getUserNameFromSubject(userId, cachedData.users))
                    .sort()
                    .join(', '),
        },
        dateColumn('created_timestamp', 'Created'),
        { field: 'created_by', headerName: 'Created By', width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        dateColumn('last_updated', 'Last Updated'),
        { field: 'last_updated_by', headerName: 'Last Updated By', width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        dateColumn('closed_timestamp', 'Closed'),
        { field: 'closed_by', headerName: 'Closed By', width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        { field: 'documents', headerName: 'Documents', width: 300, valueGetter: (_value, row) => row.documents.map((file?: UploadedFile) => file?.filename).join(', ') },
        {
            field: 'tags',
            headerName: 'Tags',
            width: 300,
            valueGetter: (_value, row) =>
                row.tags
                    .sort()
                    .map((tagId: string) => tagsMap.get(tagId)?.title ?? tagId)
                    .join(', '),
        },
        dateColumn('due_date', 'Due Date'),
        { field: 'comments', headerName: 'Comments', width: 300 },
        { field: 'link', headerName: 'Reference', width: 300 },
        { field: 'associated_risks', headerName: 'Associated Risks', width: 300, valueGetter: (_value, row) => row.associated_risks.map((associated_risk?: AssociatedRisk) => associated_risk?.title).join(', ') },
        { field: 'id', headerName: 'ID', width: 300 },
    ];

    if (actions) {
        return (
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={`/${EXPORTS}`}>Exports</BreadcrumbLink>
                        <BreadcrumbText>Actions</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Actions Export"
                body={[{ content: <DataGrid columns={columns} getRowId={(action: Action) => action.id} rows={actions} title="Actions Export Preview" fileName="Actions Export" disableVirtualization={props.disableVirtualization} /> }]}
            />
        );
    } else if (error) {
        return <Text color="darkBlue">{error}</Text>;
    } else return <Placeholder />;
};
